import React, { useId, useState } from 'react';
import axios from 'axios';
import type { FormikProps } from 'formik';
import { Formik } from 'formik';
import { Phone, PhoneInTalk, PhoneOutgoing } from 'mdi-material-ui';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import CheckboxFormik from '~/components/CheckboxFormik';
import { useClaim } from '~/components/ClaimContainer';
import mixpanel from '~/components/CmsMain/mixpanel';
import { NewPhoneCallCommunicationCard } from '~/components/communications/PhoneCall/components/PhoneCallCommunicationCard/PhoneCallCommunicationCard';
import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Tooltip from '~/components/core/Atomic/Tooltip';
import Text from '~/components/core/TextComponents/Text';
import { useCms } from '~/components/hooks/useCms';
import useInterval from '~/components/hooks/useInterval';
import PhoneIcon from '~/components/icons/notifications/PhoneIcon';
import RadioButtonFormik from '~/components/RadioButtonFormik';
import { MultiSelectTextFieldFormik, TextFieldFormik } from '~/components/TextFieldFormik';
import { getPopoverTriggerAria } from '~/components/ui/atoms/Popover/usePopoverState';
import { MIXPANEL_EVENT_SOURCES, MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { TWILIO_ADJUSTER_STATUS_DICT } from '~/Types';
import { reportAxiosError, reportErrorInProductionOrThrow } from '~/Utils';
import { yupPhoneValidation } from '~/Utils/phoneValidationUtils';

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'available':
      return '#26a56e';
    case 'available_for_queues_only':
      return '#ffbe00';
    case 'available_for_direct_calls_only':
      return '#ee8503';
    case 'not_available':
      return '#cf0000';
    case 'wrap_up':
      return '#cf0000';
    default:
      reportErrorInProductionOrThrow(`Unknown twilio status: ${status}`);
      return 'grey';
  }
};
interface TwilioStatusInfo {
  desc: string;
  can_be_chosen_manually?: boolean;
  can_accept_direct_incoming_calls?: boolean;
  can_accept_queue_calls?: boolean;
  is_required_for_queues_initialization?: boolean;
  in_call_status?: boolean;
}

interface TwilioWorkspaceDetailsExtended {
  status: keyof typeof TWILIO_ADJUSTER_STATUS_DICT;
  possible_queues: string[];
  active_queues: string[];
  possible_statuses: string[];
  is_queues_enabled: boolean;
  is_calls_forwarding_enabled?: boolean;
  forwarding_phone_number?: string;
}

interface AvailabilityFormValues {
  availability: string;
  active_queues: string[];
  possible_queues: string[];
  is_calls_forwarding_enabled: boolean;
  forwarding_phone_number: string;
}

interface UpdateAvailabilityStatusPopoverProps {
  twilioWorkspaceDetails: TwilioWorkspaceDetailsExtended;
  popoverAnchorEl: HTMLElement;
  /** The popover's id, which the status button's `aria-controls` names. */
  popoverId: string;
  onUpdate: (values: AvailabilityFormValues) => Promise<void>;
  onInitiateNewCall: () => void;
  onClose: () => void;
}

const IncomingCallAvailabilityStatus: React.FC = () => {
  const [availabilityPopoverAnchorEl, setAvailabilityPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const availabilityPopoverId = useId();
  const [showPhoneCallCommunicationCard, setShowPhoneCallCommunicationCard] = useState<boolean>(false);
  const { user, userReloadTwilioDetails, twilioWorkspaceDetails } = useCms();

  // Handle loading state since isLoadingTwilioDetails might not be available
  const isLoadingTwilioDetails = !twilioWorkspaceDetails;

  const { claim, onClaimUpdate } = useClaim();

  const isInCallStatus = twilioWorkspaceDetails
    ? (TWILIO_ADJUSTER_STATUS_DICT as Record<string, TwilioStatusInfo>)[twilioWorkspaceDetails.status]?.in_call_status
    : null;
  useInterval(userReloadTwilioDetails, isInCallStatus ? 5000 : null);

  const handleUpdateStatus = async (values: AvailabilityFormValues): Promise<void> => {
    try {
      await axios.put(`/api/v1/calls/${user.organization_id}/users/${user.id}/incoming_call_availability`, {
        ...values,
      });
      mixpanel.track(MIXPANEL_EVENTS.COMMUNICATION_AVAILABILITY_STATUS_UPDATED, {
        status: values.availability,
        active_queues: values.active_queues,
      });
    } catch (error) {
      reportAxiosError(error);
      throw error;
    }

    await userReloadTwilioDetails();
    setAvailabilityPopoverAnchorEl(null);
  };

  if (!user.is_call_center_allowed) {
    return <></>;
  }

  const commonClasses = 'border-2 border-[#1A9C9E] border-solid w-40 h-40 mr-3';

  if (isLoadingTwilioDetails) {
    return (
      <IconButton size="small" className={`${commonClasses} text-gray-500 bg-white`}>
        <Tooltip title="Loading...">
          <Phone />
        </Tooltip>
      </IconButton>
    );
  }

  if (!twilioWorkspaceDetails || (user.twilio_phone_number && !user.twilio_worker_sid)) {
    return (
      <IconButton size="small" className={`${commonClasses} text-gray-500 bg-white`}>
        <Tooltip title="Missing details - contact support">
          <Phone />
        </Tooltip>
      </IconButton>
    );
  }

  if (isInCallStatus) {
    return (
      <Tooltip title="In Active Call">
        <IconButton size="small" className={`${commonClasses} bg-white text-[#3f51b5]`}>
          <PhoneInTalk />
        </IconButton>
      </Tooltip>
    );
  }

  if (user.is_out_of_office) {
    return (
      <Tooltip title="Out Of Office">
        <IconButton size="small" className={`${commonClasses} bg-white text-[#202224]`}>
          <Phone />
        </IconButton>
      </Tooltip>
    );
  }

  const twilioStatus = twilioWorkspaceDetails.status;

  const handleChangeAvailabilityClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    setAvailabilityPopoverAnchorEl(e.currentTarget);
    mixpanel.track(MIXPANEL_EVENTS.COMMUNICATION_AVAILABILITY_STATUS_OPENED, {
      source: MIXPANEL_EVENT_SOURCES.CHANGE_AVAILABILITY_STATUS_CLICKED,
    });
  };

  const handleInitiateNewCall = (): void => {
    setShowPhoneCallCommunicationCard(true);
    setAvailabilityPopoverAnchorEl(null);
    mixpanel.track(MIXPANEL_EVENTS.NEW_PHONE_CALL_COMMUNICATION_CLICKED, {
      source: MIXPANEL_EVENT_SOURCES.NEW_PHONE_CALL_FROM_ICON_IN_AVAILABILITY_BAR,
    });
  };

  return (
    <>
      <Tooltip
        title={(TWILIO_ADJUSTER_STATUS_DICT as Record<string, TwilioStatusInfo>)[twilioStatus]?.desc || 'Unknown'}
      >
        <IconButton
          className={`${commonClasses} bg-white`}
          style={{ color: getStatusColor(twilioStatus) }}
          onClick={handleChangeAvailabilityClick}
          size="small"
          {...getPopoverTriggerAria(Boolean(availabilityPopoverAnchorEl), availabilityPopoverId)}
        >
          <Phone />
        </IconButton>
      </Tooltip>

      {availabilityPopoverAnchorEl && (
        <UpdateAvailabilityStatusPopover
          popoverAnchorEl={availabilityPopoverAnchorEl}
          popoverId={availabilityPopoverId}
          twilioWorkspaceDetails={twilioWorkspaceDetails as TwilioWorkspaceDetailsExtended}
          onUpdate={handleUpdateStatus}
          onInitiateNewCall={handleInitiateNewCall}
          onClose={() => setAvailabilityPopoverAnchorEl(null)}
        />
      )}

      {showPhoneCallCommunicationCard && (
        <NewPhoneCallCommunicationCard
          onCancel={() => setShowPhoneCallCommunicationCard(false)}
          onNewPhoneCallCreated={async (): Promise<void> => {
            if (claim) {
              if (!onClaimUpdate) {
                reportErrorInProductionOrThrow('onClaimUpdate is required but was not found in context');
              } else {
                await onClaimUpdate();
              }
            }
            setShowPhoneCallCommunicationCard(false);
          }}
          isNotClaimRelated
        />
      )}
    </>
  );
};

const UpdateAvailabilityStatusPopover = ({
  twilioWorkspaceDetails,
  popoverAnchorEl,
  popoverId,
  onUpdate,
  onInitiateNewCall,
  onClose,
}: UpdateAvailabilityStatusPopoverProps): JSX.Element => {
  const {
    possible_queues: possibleQueues,
    possible_statuses: possibleStatuses,
    is_queues_enabled: isQueuesEnabled,
  } = twilioWorkspaceDetails;

  return (
    <Formik<AvailabilityFormValues>
      initialValues={{
        availability: twilioWorkspaceDetails.status,
        active_queues: twilioWorkspaceDetails.active_queues,
        possible_queues: possibleQueues,
        is_calls_forwarding_enabled: twilioWorkspaceDetails.is_calls_forwarding_enabled || false,
        forwarding_phone_number: twilioWorkspaceDetails.forwarding_phone_number || '',
      }}
      validationSchema={Yup.object().shape({
        availability: Yup.string().required('Required'),
        active_queues: Yup.array()
          .nullable()
          .when(['availability', 'possible_queues'], {
            is: (availability: string, possible_queues: string[]) =>
              isQueuesEnabled &&
              Object.keys(TWILIO_ADJUSTER_STATUS_DICT)
                .filter(
                  (status) =>
                    (TWILIO_ADJUSTER_STATUS_DICT as Record<string, TwilioStatusInfo>)[status]?.can_accept_queue_calls
                )
                .includes(availability) &&
              possible_queues.length > 0,
            then: Yup.array().min(1, 'At least one queue required'),
          }),
        forwarding_phone_number: yupPhoneValidation.when('is_calls_forwarding_enabled', {
          is: true,
          then: yupPhoneValidation.required('Required'),
        }),
      })}
      onSubmit={async (values: AvailabilityFormValues, { setSubmitting }): Promise<void> => {
        try {
          await onUpdate(values);
        } catch (error) {
          setSubmitting(false);
        }
      }}
    >
      {(formikProps: FormikProps<AvailabilityFormValues>) => {
        const { isSubmitting, handleSubmit, values } = formikProps;

        return (
          <CardDialog
            noCardTitle
            isPopover
            closeOnBackdropClick
            open
            onClose={onClose}
            width="350px"
            anchorEl={popoverAnchorEl}
            id={popoverId}
            aria-label="Availability status"
          >
            <div className="flex w-full justify-end p-12 pt-20">
              <Button
                size="small"
                className="border-green-70 rounded-s border-1 border-solid font-bold text-green-700 shadow-xs"
                color="secondary"
                onClick={onInitiateNewCall}
              >
                <PhoneOutgoing />
                &nbsp;Initiate a new call
              </Button>
            </div>
            <div className="p-8">
              <Text variant={Text.VARIANTS.LG} className="py-12">
                Status
              </Text>
              {possibleStatuses
                .filter(
                  (status: string) =>
                    (TWILIO_ADJUSTER_STATUS_DICT as Record<string, TwilioStatusInfo>)[status]?.can_be_chosen_manually
                )
                .map((status: string) => (
                  <div key={status} className="py-12">
                    <RadioButtonFormik
                      id="availability"
                      label={(TWILIO_ADJUSTER_STATUS_DICT as Record<string, TwilioStatusInfo>)[status]?.desc || status}
                      optionValue={status}
                      size="small"
                      disabled={isSubmitting}
                    />
                    {isQueuesEnabled &&
                      possibleQueues &&
                      (TWILIO_ADJUSTER_STATUS_DICT as Record<string, TwilioStatusInfo>)[status]
                        ?.can_accept_queue_calls &&
                      values.availability === status && (
                        <div className="py-8">
                          <MultiSelectTextFieldFormik
                            id="active_queues"
                            label="Active Queues"
                            options={twilioWorkspaceDetails.possible_queues}
                            renderValue={(selected: string[]) => selected.join(', ')}
                            className="w-full"
                            fullWidth
                            renderOption={(o: string) => o}
                            disabled={isSubmitting}
                          />
                        </div>
                      )}
                  </div>
                ))}
            </div>
            {/* Call Forwarding Section */}
            <div className="mx-8 rounded border border-solid border-slate-500 bg-slate-100 p-4 px-12">
              <CheckboxFormik
                id="is_calls_forwarding_enabled"
                label={<Text variant={Text.VARIANTS.SM}>Forward my calls</Text>}
                disabled={isSubmitting}
                size="xs"
                checkboxClassName="p-8"
              />
              {values.is_calls_forwarding_enabled && (
                <div className="pl-24">
                  <TextFieldFormik
                    id="forwarding_phone_number"
                    label="Forwarding Phone Number"
                    fullWidth
                    disabled={isSubmitting}
                    InputProps={{
                      startAdornment: <PhoneIcon className="mb-4" iconColor="#666" />,
                    }}
                  />
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end space-x-2 p-12">
              <Button
                size="small"
                disabled={isSubmitting}
                variant="contained"
                color="primary"
                onClick={() => handleSubmit()}
              >
                Update
              </Button>
            </div>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

export default IncomingCallAvailabilityStatus;
